import { useState, useEffect, createContext, useContext, useCallback, ReactNode } from 'react';
import * as Sentry from '@sentry/react';
import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { App } from '@capacitor/app';
import { awsConfig, getCognitoLoginUrl, getCognitoLogoutUrl, getCognitoTokenUrl } from '../config/aws';

// ─── Types ───────────────────────────────────────────────────────

interface CognitoTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

interface UserProfile {
  id: string;
  email: string;
  role: string;
  mode: 'SINGLES' | 'COUPLES';
  isVerified: boolean;
  isActive: boolean;
  // Set when the account was registered with a verified university email.
  university: { domain: string; name: string | null } | null;
  profile: {
    displayName: string;
    dateOfBirth: string;
    bio: string | null;
    avatarUrl: string | null;
    photos: string[];
    onboardingCompleted: boolean;
    activityPreferences: Record<string, number> | null;
    budgetMidpoint: number | null;
    vibeVector: { romantic: number; adventurous: number; relaxed: number; social: number } | null;
    locationLat: number | null;
    locationLng: number | null;
    locationPostcode: string | null;
    locationRadiusMiles: number;
    gender: string | null;
    seekingGenders: string[];
    sexuality: string | null;
    relationshipGoal: string | null;
    ethnicity: string | null;
    nationality: string | null;
  } | null;
  sparkAnswers: Array<{ prompt: string; answer: string }>;
  couple: {
    id: string;
    status: string;
    partner: { id: string; displayName: string; avatarUrl: string | null } | null;
  } | null;
}

interface AuthContextType {
  cognitoUser: { sub: string; email: string; name?: string } | null;
  userProfile: UserProfile | null;
  loading: boolean;
  isAuthenticated: boolean;
  isOnboarded: boolean;
  // True when we're authenticated but couldn't load the profile for a reason
  // OTHER than "no account" (rate limit, 5xx, offline). Callers must NOT treat
  // this as a new user — see the comment on fetchProfile.
  profileUnavailable: boolean;
  signInWithGoogle: () => void;
  signInWithFacebook: () => void;
  signOut: () => void;
  getIdToken: () => Promise<string | null>;
  refreshProfile: () => Promise<void>;
  handleTokens: (idToken: string, accessToken: string, refreshToken: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8080';
const TOKEN_KEY = 'preminade_tokens';

// ─── Token Management ────────────────────────────────────────────

function storeTokens(tokens: CognitoTokens): void {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens));
}

function getStoredTokens(): CognitoTokens | null {
  const raw = localStorage.getItem(TOKEN_KEY);
  if (!raw) return null;
  try {
    const tokens = JSON.parse(raw) as CognitoTokens;
    // Expired but has refresh token — still return it so we can try to refresh
    return tokens;
  } catch { return null; }
}

function clearTokens(): void {
  localStorage.removeItem(TOKEN_KEY);
}

function decodeJwtPayload(token: string): any {
  try {
    if (token.startsWith('mock.')) {
      // "mock.<id>" or "mock.<id>~<email>" — mirrors parseMockToken() in the
      // backend's auth middleware. Parsed after "mock." because emails have dots.
      const body = token.slice('mock.'.length);
      const tilde = body.indexOf('~');
      return tilde === -1
        ? { sub: body, email: 'mock@preminade.dev', name: 'Mock User' }
        : { sub: body.slice(0, tilde), email: body.slice(tilde + 1), name: 'Mock User' };
    }
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64));
  } catch { return null; }
}

// ─── OAuth2 Authorization Code Exchange ──────────────────────────

async function exchangeCodeForTokens(code: string): Promise<CognitoTokens> {
  const { clientId, redirectUri } = awsConfig.cognito;

  const res = await fetch(getCognitoTokenUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: clientId,
      redirect_uri: redirectUri,
      code,
    }),
  });

  if (!res.ok) {
    const err = await res.text();
    throw new Error(`Token exchange failed: ${err}`);
  }

  const data = await res.json();
  return {
    idToken: data.id_token,
    accessToken: data.access_token,
    refreshToken: data.refresh_token,
    expiresAt: Date.now() + (data.expires_in * 1000),
  };
}

async function refreshAccessToken(refreshToken: string): Promise<CognitoTokens | null> {
  try {
    const { clientId } = awsConfig.cognito;
    const res = await fetch(getCognitoTokenUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        client_id: clientId,
        refresh_token: refreshToken,
      }),
    });

    if (!res.ok) return null;

    const data = await res.json();
    return {
      idToken: data.id_token,
      accessToken: data.access_token,
      refreshToken,  // Cognito doesn't rotate refresh tokens
      expiresAt: Date.now() + (data.expires_in * 1000),
    };
  } catch { return null; }
}

// ─── Provider ────────────────────────────────────────────────────

export function AuthProvider({ children }: { children: ReactNode }) {
  const [cognitoUser, setCognitoUser] = useState<{ sub: string; email: string; name?: string } | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [profileUnavailable, setProfileUnavailable] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tokens, setTokens] = useState<CognitoTokens | null>(null);

  // Fetch the user profile from our backend.
  //
  // CRITICAL: only a 404 means "this account doesn't exist yet" (→ onboarding).
  // Every other failure (429 rate limit, 5xx, cold start, offline) is transient
  // and must NOT clear userProfile — doing so previously made the app render
  // Onboarding for fully onboarded users, who then saw no matches and no couple
  // and reasonably concluded their account had been wiped.
  const fetchProfile = useCallback(async (idToken: string): Promise<void> => {
    const MAX_ATTEMPTS = 3;
    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
      try {
        const res = await fetch(`${API_BASE}/api/auth/me`, {
          headers: { Authorization: `Bearer ${idToken}` },
        });
        if (res.ok) {
          const data = await res.json();
          setUserProfile(data.user);
          setProfileUnavailable(false);
          return;
        }
        if (res.status === 404) {
          // Genuinely no account yet — onboarding is the correct destination.
          setUserProfile(null);
          setProfileUnavailable(false);
          return;
        }
        // 401 means the token is bad — no amount of retrying helps.
        if (res.status === 401) {
          setProfileUnavailable(true);
          return;
        }
      } catch {
        // Network error — fall through to retry.
      }
      if (attempt < MAX_ATTEMPTS - 1) {
        await new Promise(r => setTimeout(r, 600 * Math.pow(2, attempt))); // 600ms, 1.2s
      }
    }
    // Exhausted retries: we still don't know the account state. Leave whatever
    // profile we had intact and let the UI show a reconnect prompt.
    setProfileUnavailable(true);
  }, []);

  // Initialize — check for stored tokens or OAuth callback code
  useEffect(() => {
    const init = async () => {
      // Check for OAuth callback code in URL
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');

      if (code) {
        try {
          const newTokens = await exchangeCodeForTokens(code);
          storeTokens(newTokens);
          setTokens(newTokens);

          const payload = decodeJwtPayload(newTokens.idToken);
          setCognitoUser({
            sub: payload.sub,
            email: payload.email,
            name: payload.name || payload['cognito:username'],
          });

          await fetchProfile(newTokens.idToken);

          // Clean URL
          window.history.replaceState({}, '', window.location.pathname);
        } catch (err) {
          Sentry.captureException(err, { tags: { flow: 'oauth_callback' } });
          console.error('OAuth callback failed:', err);
        }
      } else {
        // Check for existing tokens
        const stored = getStoredTokens();
        if (stored) {
          // Expired or close to expiry — try refresh first
          if (stored.expiresAt - Date.now() < 5 * 60 * 1000) {
            const refreshed = await refreshAccessToken(stored.refreshToken);
            if (refreshed) {
              storeTokens(refreshed);
              setTokens(refreshed);
              const payload = decodeJwtPayload(refreshed.idToken);
              setCognitoUser({ sub: payload.sub, email: payload.email, name: payload.name });
              await fetchProfile(refreshed.idToken);
            } else {
              clearTokens();
            }
          } else {
            // Token still valid — set user synchronously from JWT, fetch profile in parallel
            const payload = decodeJwtPayload(stored.idToken);
            setCognitoUser({ sub: payload.sub, email: payload.email, name: payload.name });
            setTokens(stored);
            await fetchProfile(stored.idToken);
          }
        }
      }

      setLoading(false);
    };

    init();

    // On native, Cognito redirects to com.preminade.app://callback?code=...
    // Capacitor fires appUrlOpen — extract the code and exchange it.
    if (Capacitor.isNativePlatform()) {
      const handleDeepLink = async ({ url }: { url: string }) => {
        const params = new URLSearchParams(url.split('?')[1] || '');
        const code = params.get('code');
        if (!code) return;
        try {
          await Browser.close();
          const newTokens = await exchangeCodeForTokens(code);
          storeTokens(newTokens);
          setTokens(newTokens);
          const payload = decodeJwtPayload(newTokens.idToken);
          setCognitoUser({ sub: payload.sub, email: payload.email, name: payload.name || payload['cognito:username'] });
          await fetchProfile(newTokens.idToken);
        } catch (err) {
          console.error('[auth] deep link token exchange failed:', err);
        }
      };

      App.addListener('appUrlOpen', handleDeepLink);
    }
  }, [fetchProfile]);

  // Auto-refresh tokens before expiry
  useEffect(() => {
    if (!tokens) return;
    const timeUntilRefresh = tokens.expiresAt - Date.now() - 5 * 60 * 1000;
    if (timeUntilRefresh <= 0) return;

    const timer = setTimeout(async () => {
      const refreshed = await refreshAccessToken(tokens.refreshToken);
      if (refreshed) {
        storeTokens(refreshed);
        setTokens(refreshed);
      } else {
        clearTokens();
        setCognitoUser(null);
        setUserProfile(null);
      }
    }, timeUntilRefresh);

    return () => clearTimeout(timer);
  }, [tokens]);

  const openCognitoUrl = (url: string) => {
    if (Capacitor.isNativePlatform()) {
      Browser.open({ url, windowName: '_self' });
    } else {
      window.location.href = url;
    }
  };

  const signInWithGoogle = () => openCognitoUrl(getCognitoLoginUrl('Google'));
  const signInWithFacebook = () => openCognitoUrl(getCognitoLoginUrl('Facebook'));

  const signOut = () => {
    clearTokens();
    setCognitoUser(null);
    setUserProfile(null);
    setTokens(null);
    openCognitoUrl(getCognitoLogoutUrl());
  };

  const getIdToken = async (): Promise<string | null> => {
    if (!tokens) return null;
    // Refresh if expired
    if (tokens.expiresAt < Date.now() + 60 * 1000) {
      const refreshed = await refreshAccessToken(tokens.refreshToken);
      if (refreshed) {
        storeTokens(refreshed);
        setTokens(refreshed);
        return refreshed.idToken;
      }
      return null;
    }
    return tokens.idToken;
  };

  const refreshProfile = async () => {
    const token = await getIdToken();
    if (token) await fetchProfile(token);
  };

  const handleTokens = async (idToken: string, accessToken: string, refreshToken: string) => {
    const newTokens: CognitoTokens = { idToken, accessToken, refreshToken, expiresAt: Date.now() + 3600 * 1000 };
    storeTokens(newTokens);
    setTokens(newTokens);
    const payload = decodeJwtPayload(idToken);
    setCognitoUser({ sub: payload.sub, email: payload.email, name: payload.name || payload['cognito:username'] });
    await fetchProfile(idToken);
  };

  const isAuthenticated = !!cognitoUser;
  const isOnboarded = !!userProfile?.profile?.onboardingCompleted;

  return (
    <AuthContext.Provider value={{
      cognitoUser, userProfile, loading, isAuthenticated, isOnboarded, profileUnavailable,
      signInWithGoogle, signInWithFacebook, signOut,
      getIdToken, refreshProfile, handleTokens,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) throw new Error('useAuth must be used within an AuthProvider');
  return context;
}

// ─── API Helper ──────────────────────────────────────────────────

export function useApi() {
  const { getIdToken } = useAuth();

  const apiFetch = async (path: string, options: RequestInit = {}) => {
    const token = await getIdToken();
    const res = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        ...options.headers,
      },
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: 'Request failed' }));
      throw new Error(error.error || `API error: ${res.status}`);
    }

    return res.json();
  };

  return { apiFetch };
}
